import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertPopupComponent } from '../../../modules/components/alert-popup/alert-popup.component';
import { LoaderComponent } from '../../../modules/components/loader/loader.component';
import { LoginService } from '../service/login.service';
import { UserService } from '../service/user.service';

/**
 * Displays the expired-link recovery screen for users who need to resend a password reset link and continue the reset flow.
 * @component
 * @selector app-linked-expired
 * @example
 *   <app-linked-expired></app-linked-expired>
 * @input string email - Email address used to resend the password reset link; may be populated from query params or fetched from the token.
 * @input string resetToken - Reset token extracted from the route query params and used to verify or resend the link.
 * @input boolean isLoading - Indicates whether the resend-link request is in progress.
 * @input boolean showHelpDetails - Controls visibility of the help/details section.
 * @output none - This component does not expose output events; it updates internal state and uses routing/service calls.
 * @method ngOnInit - Reads query parameters on initialization and triggers token/email verification flow.
 * @method fetchEmailFromToken - Retrieves the email and reset token from the API when only a token is available.
 * @method fetchVerifierForResetToken - Fetches and stores the verifier associated with the reset token for the reset workflow.
 * @method onResendLink - Sends a new password reset link to the user's email and updates alert/loading state.
 * @method onBackToLogin - Navigates the user back to the login page.
 * @method closeAlert - Clears the current alert message and visibility state.
 * @method toggleHelpDetails - Toggles the help/details panel visibility.
 * @lifecycle ngOnInit - Primary lifecycle hook used to initialize component state from route query parameters.
 */
@Component({
  selector: 'app-linked-expired',
  standalone: true,
  imports: [CommonModule, ButtonModule, AlertPopupComponent, LoaderComponent, TranslateModule],
  templateUrl: './linked-expired.component.html',
  styleUrl: './linked-expired.component.css'
})
export class LinkedExpiredComponent {
  logoImageUrl = 'assets/vectors/RegTrackLogo.svg';
  showHelpDetails = false;
  email: string = '';
  resetToken: string = '';
  isLoading: boolean = false;
  alertData = { alertMessage: '', alertVisible: '' };

  constructor(private router: Router, private loginService: LoginService, private route: ActivatedRoute, private user: UserService, private translate: TranslateService) { }

  /**
   * Initializes the component by reading query parameters and triggering token-related API flows.
   * @example
   * gOnInit()
   * void
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const token = params['data'] || '';
      this.resetToken = token;
      this.email = params['email'] || '';

      if (token) {
        if (!this.email) {
          //fetch email and token from API
          this.fetchEmailFromToken(token);
        } else {
          //fetch verifier for HMAC
          this.fetchVerifierForResetToken(token);
        }
      }
    });
  }

  /**
  * Fetches the user's email and reset token associated with a verification token.
  * @example
  * etchEmailFromToken('abc123token')
  * void
  * @param {string} token - The verification token used to retrieve the associated email and reset token.
  * @returns {void} No return value.
  **/
  fetchEmailFromToken(token: string): void {
    const encodedToken = encodeURIComponent(token);
    this.loginService.verifyLinkExpiry(encodedToken).subscribe({
      next: (response: HttpResponse<any>) => {
        // API returns user_email and token in body even when success: false
        this.email = response.body?.user_email || '';
        this.resetToken = response.body?.token || token;
        this.fetchVerifierForResetToken(this.resetToken);
      },
      error: (error: any) => {
        console.error('Error fetching email from token:', error);
      }
    });
  }

  /**
  * Verifies a reset token by fetching the associated verifier and storing it in the user service.
  * @example
  * etchVerifierForResetToken('abc123token')
  * void
  * @param {string} token - The reset token used to retrieve the verifier.
  * @returns {void} No return value.
  **/
  fetchVerifierForResetToken(token: string): void {
    this.loginService.getIdentifiersWithToken(token).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.body?.result?.verifier) {
          const verifier = atob(response.body.result.verifier);
          this.user.setVerifier(verifier);
        } else {
          console.warn('No verifier in response');
        }
      },
      error: (error: any) => {
        console.error('Error fetching verifier:', error);
      }
    });
  }

  /**
  * Resends the password reset link to the user's email address.
  * @example
  * nResendLink()
  * void
  * @returns {void} This function does not return a value.
  **/
  onResendLink(): void {
    this.isLoading = true;
    const params = { email: this.email };

    this.loginService.resetPassword(params, true, this.resetToken).subscribe({
      next: (response:any) => {
        this.isLoading = false;
        if (response.status === 200) {
          this.alertData = { alertMessage: this.translate.instant('app.linked_expired.reset_link_sent'), alertVisible: 'success' };
        } else {
          this.alertData = { alertMessage: this.translate.instant('app.linked_expired.something_went_wrong'), alertVisible: 'error' };
        }
      },
      error: (error:any) => {
        this.isLoading = false;
        this.alertData = { alertMessage: this.translate.instant('app.linked_expired.something_went_wrong'), alertVisible: 'error' };
        console.error("Error while resending link:", error);
      }
    });
  }

  onBackToLogin(): void {
    this.router.navigate(['/login']);
  }

  closeAlert(): void {
    this.alertData = { alertMessage: '', alertVisible: '' };
  }

  toggleHelpDetails(): void {
    this.showHelpDetails = !this.showHelpDetails;
  }
}
